import React, { type FC, type ReactNode } from 'react'
import { Pressable, View } from 'react-native'
import type { GestureResponderEvent, TextStyle, StyleProp } from 'react-native'

import withMouseInteractions, {
  type UserInteractiveStateProps,
} from '../../../diagnostics/higher-order-components/withMouseInteractions'
import CustomIcon from '../../../native-components/CustomIcon'
import { Text } from '../../../native-components/text-styles'

import { useComponentStyle, useThemeContext } from '../../../themes'

import { scrubTestID } from '../../../utils/testUtils'

import { Spinner } from '../spinner'

import type { ButtonTheme } from '.'
import type {
  ComponentThemeFactory,
  ComponentThemeProps,
} from '../../../themes/util/types'
import { colors, baseColors } from '../../../themes/techapp-light'

declare module '../../../themes/util/types' {
  interface ComponentThemes {
    Button: ButtonTheme
  }
}

type BaseButtonProps = ComponentThemeProps<ButtonTheme> &
  Partial<UserInteractiveStateProps> & {
    enabled?: boolean
    onPress?: (event: GestureResponderEvent) => void
    testID?: string
    selected?: boolean
  }

interface ButtonWithTextProps extends BaseButtonProps {
  text: string
  children?: never
  icon?: never
  showSpinner?: boolean
}

interface IconOnlyButtonProps extends BaseButtonProps {
  text?: never
  children?: never
  icon: string
  showSpinner?: boolean
}

interface IconButtonWithTextProps extends BaseButtonProps {
  icon: string
  text: string
  children?: never
  showSpinner?: boolean
}

interface ButtonWithChildrenProps extends BaseButtonProps {
  children:
    | ReactNode
    | (({
        pressed,
        textStyles,
      }: {
        pressed: boolean
        textStyles: StyleProp<TextStyle>
      }) => ReactNode)
  text?: never
  icon?: never
  showSpinner?: boolean
}

interface ButtonWithSpinner extends BaseButtonProps {
  text?: string
  icon?: string
  children?: never
  showSpinner: boolean
}

export type ButtonProps =
  | ButtonWithTextProps
  | ButtonWithChildrenProps
  | IconButtonWithTextProps
  | IconOnlyButtonProps
  | ButtonWithSpinner

const baseButtonTheme: ComponentThemeFactory<ButtonTheme> = ({ isWeb }) => ({
  button: [
    {
      style: {
        flexDirection: 'row',
        width: 287,
        justifyContent: 'center',
        marginTop: 8,
        alignSelf: 'center',
        maxWidth: '100%',
        padding: 8,
        alignItems: 'center',
      },
    },
    {
      layout: 'fullWidth',
      style: { width: '100%' },
    },
    {
      layout: 'fullWidthLimited',
      style: {
        width: '100%',
        alignSelf: 'center',
        maxWidth: isWeb ? 270 : 550,
      },
    },
    {
      layout: 'inputAction',
      style: {
        paddingLeft: 12,
        paddingRight: 12,
        marginTop: 0,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        justifyContent: 'center',
        width: 'auto',
      },
    },
    {
      decoration: 'textOnly',
      style: {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
      },
    },
    {
      layout: 'inline',
      style: {
        width: 'auto',
      },
    },
    {
      layout: 'grouped',
      style: {
        width: 'auto',
        flex: 1,
      },
    },
    {
      decoration: 'secondary',
      style: {
        backgroundColor: 'transparent',
        borderWidth: 1,
      },
    },
    {
      decoration: 'danger-secondary',
      style: {
        backgroundColor: 'transparent',
        borderWidth: 1,
      },
    },
  ],
  text: [
    {
      style: {
        fontSize: 22,
      },
    },
    {
      layout: 'inputAction',
      style: {
        textAlign: 'center',
        fontSize: 21,
      },
    },
    {
      layout: 'grouped',
      style: {
        width: '100%',
        textAlign: 'center',
      },
    },
  ],
  iconContainer: [
    {
      style: {
        marginRight: 5,
        alignItems: 'center',
        width: 22,
        height: 22,
      },
    },
  ],
  spinner: [],
})

const Button: FC<ButtonProps> = ({
  styles,
  decoration,
  layout,
  text,
  children,
  enabled = true,
  showSpinner = false,
  testID,
  selected,
  icon,
  ...props
}) => {
  const { screenSize } = useThemeContext()

  const buttonStyles = useComponentStyle(
    'Button',
    baseButtonTheme,
    styles,
    layout,
    decoration,
    selected === true ? 'selected'
    : selected === false ? 'deselected'
    : enabled ?
      props.mouseDown ? 'active'
      : props.hovered ? 'hovered'
      : 'enabled'
    : 'disabled',
    screenSize,
  )

  const buttonFocusedStyles = useComponentStyle(
    'Button',
    baseButtonTheme,
    styles,
    layout,
    decoration,
    'focused',
    screenSize,
  )

  const currentButtonStyles =
    props.focused ?
      {
        ...buttonStyles,
        button: [buttonFocusedStyles.button, buttonStyles.button],
      }
    : buttonStyles
  const { color: spinnerActiveColor = colors.primary } =
    currentButtonStyles.text as TextStyle
  const { backgroundColor: spinnerBorderColor = baseColors.white } =
    currentButtonStyles.button as TextStyle
  return (
    <Pressable
      role="button"
      disabled={!enabled || showSpinner}
      style={currentButtonStyles.button}
      testID={scrubTestID(testID ?? text, 'button')}
      {...props}
    >
      {showSpinner ?
        <Spinner
          decoration={decoration}
          spinnerActiveColor={spinnerActiveColor}
          spinnerBorderColor={spinnerBorderColor}
        />
      : children && typeof children === 'function' ?
        children({
          textStyles: currentButtonStyles.text,
          pressed: props.mouseDown ?? false,
          ...props,
        })
      : (children ?? (
          <>
            {icon && (
              <View
                style={[
                  currentButtonStyles.iconContainer,
                  currentButtonStyles.text,
                ]}
              >
                <CustomIcon name={icon} />
              </View>
            )}
            <Text
              style={currentButtonStyles.text}
              {...(decoration === 'textOnly' && { onPress: props.onPress })}
              testID={scrubTestID(testID ?? text, 'button-text')}
            >
              {text}
            </Text>
          </>
        ))
      }
    </Pressable>
  )
}

export default withMouseInteractions(Button)
